import { useState } from 'react';
import { X } from 'lucide-react';
import { ErreurApi } from '../../lib/api';
import {
  usePositionnementsCandidat,
  usePositionner,
  type LigneMatching,
} from '../../lib/useMatching';
import { libellePositionnement } from '../../lib/immersion-statut';
import { ChampRecherchable } from '../ui/ChampRecherchable';
import { Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { FormulairePositionnement } from '../immersion/FormulairePositionnement';

function messageErreur(erreur: unknown): string | null {
  return erreur instanceof ErreurApi ? erreur.message : null;
}

/**
 * Entreprise choisie à la main pour un candidat donné. Les entreprises
 * proposées sont celles de son antenne, et l'API dit d'avance ce qui
 * empêcherait de le positionner : l'écran l'affiche au lieu du formulaire.
 */
function PositionnementDuCandidat({
  apprenantId,
  peutCreerEntreprise,
  onPositionne,
}: {
  apprenantId: string;
  peutCreerEntreprise: boolean;
  onPositionne: () => void;
}): JSX.Element {
  const notifier = useToast();
  const { data, isLoading, error } = usePositionnementsCandidat(apprenantId);
  const positionner = usePositionner();

  if (isLoading) return <Chargement libelle="Chargement des entreprises de l’antenne…" />;
  if (error || !data) {
    return <MessageErreur message="Les entreprises de l’antenne n’ont pas pu être chargées." />;
  }
  if (data.refusPositionnement) {
    return (
      <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
        {data.refusPositionnement}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <FormulairePositionnement
        entreprises={data.entreprises}
        {...(peutCreerEntreprise ? { creationEntreprise: { site: data.site } } : {})}
        enCours={positionner.isPending}
        onValider={({ entrepriseId, ...saisie }) =>
          positionner.mutate(
            { ...saisie, apprenantId, entrepriseId: entrepriseId ?? '' },
            {
              onSuccess: ({ message }) => {
                notifier(message);
                onPositionne();
              },
            },
          )
        }
      />
      {messageErreur(positionner.error) && (
        <p role="alert" className="text-[11px] text-red-700">
          {messageErreur(positionner.error)}
        </p>
      )}
    </div>
  );
}

/**
 * Positionnement hors des suggestions du moteur (TF-334) : le candidat et
 * l'entreprise se choisissent à la main. Même formulaire et même route que la
 * fiche d'entretien — aucune suggestion n'est requise, et le positionnement
 * n'est rattaché à aucun besoin.
 *
 * Pas de `Carte` : son `overflow-hidden` couperait la liste des entreprises.
 */
export function PositionnementManuel({
  candidats,
  apprenantIdInitial,
  peutCreerEntreprise,
  onFermer,
}: {
  /** Candidats du croisement : les seuls positionnables sur le périmètre. */
  candidats: LigneMatching[];
  apprenantIdInitial: string | null;
  peutCreerEntreprise: boolean;
  onFermer: () => void;
}): JSX.Element {
  const [apprenantId, setApprenantId] = useState(apprenantIdInitial ?? '');

  return (
    <section
      aria-label="Positionnement manuel"
      className="rounded-2xl bg-white shadow-card"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-codialis-navy">Positionnement manuel</h3>
          <p className="text-[11px] text-codialis-gray">
            Un candidat et une entreprise de son antenne, choisis sans passer par les
            suggestions.
          </p>
        </div>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer le positionnement manuel"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-codialis-gray transition hover:bg-gray-50 hover:text-codialis-navy"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div className="space-y-4 p-4 sm:p-6">
        <div className="max-w-xl">
          <ChampRecherchable
            id="positionnement-manuel-candidat"
            libelle="Candidat"
            placeholder="Nom, formation ou antenne…"
            options={candidats.map((ligne) => ({
              id: ligne.apprenantId,
              libelle: ligne.candidat,
              detail: [ligne.formation, ligne.siteCode, ligne.ville].filter(Boolean).join(' · '),
              ...(ligne.immersion
                ? {
                    complement: (
                      <Pastille
                        texte={libellePositionnement(ligne.immersion).texte}
                        ton={libellePositionnement(ligne.immersion).ton}
                      />
                    ),
                  }
                : {}),
            }))}
            valeur={apprenantId || null}
            onChoisir={setApprenantId}
            aucunResultat="Aucun candidat à positionner ne correspond."
            classeLibelle="text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
          />
        </div>
        {apprenantId ? (
          <PositionnementDuCandidat
            key={apprenantId}
            apprenantId={apprenantId}
            peutCreerEntreprise={peutCreerEntreprise}
            onPositionne={onFermer}
          />
        ) : (
          <p className="text-sm text-codialis-gray">
            Choisissez d’abord le candidat : les entreprises proposées sont celles de son
            antenne.
          </p>
        )}
      </div>
    </section>
  );
}
